import { useQuery } from "@apollo/client";
import ArrowForward from "@mui/icons-material/ArrowForward";
import {
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { LineItemFragment } from "generated-graphql/graphql";
import { Link as RouterLink, useParams } from "react-router-dom";
import { prettyPrintMonthYear } from "util/dates";

import { EPISODIC_EVENT_MANIFESTS_QUERY } from "../EpisodicEvents/api";
import { formatPounds } from "../EpisodicEvents/episodicDisplay";

type LineEpisodicEvent = LineItemFragment["episodicExemptions"][number]["episodicEvent"];

function EventOnManifest({
  event,
  manifestTrackingNumber,
  tenantId,
}: {
  event: LineEpisodicEvent;
  manifestTrackingNumber: string;
  tenantId: string | undefined;
}) {
  const { data } = useQuery(EPISODIC_EVENT_MANIFESTS_QUERY, { variables: { id: event.id } });
  const manifest = data?.episodicEvent?.manifestExemptions.manifests.find(
    (m) => m.manifestTrackingNumber === manifestTrackingNumber
  );
  if (!manifest) {
    return null;
  }
  const shipMonth = manifest.shippedDate ? prettyPrintMonthYear(manifest.shippedDate) : "—";
  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Typography variant="body2" color="text.secondary">
          {event.reason}
        </Typography>
        <Button
          size="small"
          component={RouterLink}
          to={`/o/${tenantId}/waste/episodic-events?facilityId=${event.facilityId}`}
          endIcon={<ArrowForward />}
        >
          View event
        </Button>
      </Stack>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Waste stream</TableCell>
            <TableCell align="right">Episodic</TableCell>
            <TableCell>Ship month</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {manifest.streams.map((stream) => (
            <TableRow key={stream.wasteStream.id}>
              <TableCell>{stream.wasteStream.name}</TableCell>
              <TableCell align="right">
                {`${formatPounds(stream.exemptPounds)} of ${formatPounds(stream.groupPounds)}`}
              </TableCell>
              <TableCell>{shipMonth}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

export function LineEpisodicSection({
  exemptions,
  manifestTrackingNumber,
  tenantId: tenantIdProp,
}: {
  exemptions: LineItemFragment["episodicExemptions"];
  manifestTrackingNumber: string;
  tenantId?: string;
}) {
  const { tenantId: routeTenantId } = useParams<{ tenantId: string }>();
  const tenantId = tenantIdProp ?? routeTenantId;
  return (
    <Box sx={{ bgcolor: "action.hover", borderRadius: 1, p: 2 }}>
      <Typography variant="subtitle2">Episodic waste on this manifest</Typography>
      <Stack spacing={2}>
        {exemptions.map(({ episodicEvent }) => (
          <EventOnManifest
            key={episodicEvent.id}
            event={episodicEvent}
            manifestTrackingNumber={manifestTrackingNumber}
            tenantId={tenantId}
          />
        ))}
      </Stack>
    </Box>
  );
}
